import {
  Component,
  ChangeDetectionStrategy,
  Input,
  HostBinding,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { NgxDeepEqualsPureService } from 'ngx-deep-equals-pure';
import { PerfectScrollbarDirective } from 'ngx-perfect-scrollbar';
import { Subject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ChatMessage } from '../models';
import { ChatService } from '../services';
import { AuthService, Unity4Claim } from '../../auth/';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'lib-chat-messages',
  templateUrl: './chat-messages.component.html',
  styles: [
    `
      .chat-supervisor {
        font-weight: bold;
        background-color: #6c757d;
        color: #e9ecef;
      }

      .chat-messages {
        border-right: 1px solid #67757c;
      }

      .chat-scroll {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        overflow: auto;
        padding-left: 5px;
        padding-right: 10px;
      }

      .small-avatar {
        min-width: 31px;
        max-width: 31px;
        min-height: 31px;
        max-height: 31px;
        overflow: hidden;
      }

      .border-8 {
        border-radius: 8px !important;
      }

      .extra-small {
        font-size: 60%;
      }

      .ml-31 {
        margin-left: 31px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesComponent implements OnChanges, OnDestroy {
  @HostBinding('class') class = 'd-flex flex-column flex-grow-1';
  @Input() currentUserId?: number;
  @Input() messages?: ChatMessage[];
  @ViewChild(PerfectScrollbarDirective, { static: false })
  scrollBar?: PerfectScrollbarDirective;
  @Input() showGroup: boolean = false;
  @Input() showSettings: boolean = false;

  private currentTip?: NgbTooltip;
  private personalTip?: NgbTooltip;
  private scrollToBottom = new Subject<void>();
  private subs = new SubSink();

  autoScroll: boolean = true;
  showToolTip$ = this.authSvc.hasClaim$(Unity4Claim.ChatSupervisor);

  constructor(
    private deepEqualsSvc: NgxDeepEqualsPureService,
    private chatSvc: ChatService,
    private authSvc: AuthService,
    private toastr: ToastrService
  ) {
    this.subs.sink = this.scrollToBottom.pipe(delay(100)).subscribe(() => {
      this.scrollBar?.update();
      if (this.autoScroll) {
        this.scrollBar?.scrollToBottom();
      }
    });
  }

  confirmPersonalTooltip(userid: number) {
    this.chatSvc.chatUserName$(userid).subscribe((uname) => {
      this.chatSvc
        .confirm(
          'Open Personal Chat',
          'Are you sure you wish to open a Personal Chat with ' + uname + '?'
        )
        .then((confirmresult) => {
          if (confirmresult) {
            this.chatSvc.addPrivateChatroom(userid, this.currentUserId!);

            this.personalTip?.close();
          }
        });
    });
  }

  isChatSupervisor(userid: number) {
    return this.chatSvc.isChatSupervisor(userid);
  }

  messageById(index: number, item: ChatMessage) {
    return item.messageId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.messages &&
      (changes.messages.firstChange ||
        !this.deepEqualsSvc.deepEquals(
          changes.messages.previousValue,
          changes.messages.currentValue
        ))
    ) {
      this.scrollToBottom.next();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  pin(messageId: number, tip: NgbTooltip) {
    this.chatSvc.pinMessage(messageId);
    tip.close();
  }

  showPersonalTip(tip: NgbTooltip) {
    if (this.personalTip && this.personalTip !== tip) {
      this.personalTip?.close();
    }
    this.personalTip = tip;
    tip.open();
  }

  showTip(tip: NgbTooltip) {
    if (!this.personalTip?.isOpen()) {
      if (this.currentTip && this.currentTip !== tip) {
        this.currentTip?.close();
      }
      this.currentTip = tip;
      tip.open();
    }
  }

  startAutoScroll(event: any) {
    this.autoScroll = true;
    this.currentTip?.close();
  }

  stopAutoScroll(event: any) {
    if (this.scrollBar?.ps()?.reach.y !== 'end') {
      this.autoScroll = false;
    }
    this.currentTip?.close();
  }
}
